import i18n from '../../i18n';
import { BaseApiClient } from './base-client';

/**
 * Type for a persistent user element (character/object)
 */
export interface UserElement {
  id: string;
  name: string;
  frontalImageUrl: string;
  frontalImageS3Key?: string;
  referenceImages: Array<{ s3Key: string; s3Url: string }>;
  klingElementId?: number | null;
  createdAt: string;
}

export interface UserElementsResponse {
  elements: UserElement[];
  limit: number;
  current: number;
}

export interface UploadedImage {
  s3Key: string;
  s3Url: string;
}

/**
 * User Elements API - persistent library of characters/objects for Kling 3.0
 */
export class UserElementsApi extends BaseApiClient {

  /**
   * Get all user's saved elements with subscription limits
   */
  async getUserElements(teamId?: string | null): Promise<UserElementsResponse> {
    try {
      const params = teamId ? { teamId } : {};
      const response = await this.client.get('/api/video/elements', { params });
      return {
        elements: response.data.elements || [],
        limit: response.data.limit ?? 1,
        current: response.data.current ?? 0
      };
    } catch (error: any) {
      console.error('Error fetching user elements:', error);
      return { elements: [], limit: 1, current: 0 };
    }
  }

  /**
   * Upload a single image to S3 immediately.
   * Call this as soon as the user selects a photo — don't wait for "Create".
   * @param file - Image file
   * @param type - 'frontal' or 'ref'
   * @param onProgress - Upload progress callback (0-100)
   */
  async uploadElementImage(
    file: File,
    type: 'frontal' | 'ref' = 'frontal',
    onProgress?: (progress: number) => void,
    teamId?: string | null
  ): Promise<UploadedImage> {
    const formData = new FormData();
    formData.append('image', file);
    if (teamId) formData.append('teamId', teamId);

    const response = await this.client.post(
      `/api/video/elements/upload-image?type=${type}`,
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 30000,
        onUploadProgress: (e) => {
          if (onProgress && e.total) {
            onProgress(Math.round((e.loaded / e.total) * 100));
          }
        }
      }
    );

    if (!response.data.success) {
      throw new Error(response.data.error || 'Upload failed');
    }

    return {
      s3Key: response.data.s3Key,
      s3Url: response.data.s3Url
    };
  }

  /**
   * Create a new element from pre-uploaded S3 images.
   * Images should already be uploaded via uploadElementImage().
   */
  async createElement(
    name: string,
    frontal: File | UploadedImage,
    references: (File | UploadedImage)[],
    teamId?: string | null
  ): Promise<UserElement | null> {
    try {
      // If frontal is a raw File, upload it first
      const frontalUploaded: UploadedImage = frontal instanceof File
        ? await this.uploadElementImage(frontal, 'frontal')
        : frontal;

      // Upload any raw File references
      const refsUploaded: UploadedImage[] = await Promise.all(
        references.map(r => r instanceof File ? this.uploadElementImage(r, 'ref') : r)
      );

      const response = await this.client.post('/api/video/elements', {
        name,
        frontalS3Key: frontalUploaded.s3Key,
        frontalS3Url: frontalUploaded.s3Url,
        referenceImages: refsUploaded,
        ...(teamId && { teamId }),
      }, { timeout: 15000 });

      if (response.data.success) {
        return response.data.element;
      }
      return null;
    } catch (error: any) {
      console.error('Error creating element:', error);
      const message = error.response?.data?.error || i18n.t('common:error.createElement');
      throw new Error(message);
    }
  }

  /**
   * Delete an element and its S3 files
   */
  async deleteElement(elementId: string): Promise<void> {
    try {
      await this.client.delete(`/api/video/elements/${elementId}`);
    } catch (error: any) {
      console.error('Error deleting element:', error);
      throw new Error(i18n.t('common:error.deleteElement'));
    }
  }
}
